import {Card, useMediaQuery, useTheme} from '@material-ui/core'
import {Grid, Hidden, Dialog, DialogContent} from '@mui/material'
import parsePhoneNumberFromString, {CountryCode} from 'libphonenumber-js'
import {isEmpty, values, first} from 'lodash'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useForm, useFieldArray, FormProvider} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {useLocation} from 'react-router-dom'

import {trackEvent} from '../../../common/analytics'
import {usePostOrderRequestDelivery} from '../../../common/react-query/hooks/mutations/usePostOrderRequestDelivery'
import {usePostOrderRequestDeliveryV2} from '../../../common/react-query/hooks/mutations/usePostOrderRequestDeliveryV2'
import {usePersistedContact} from '../../../common/react-query/hooks/queries/usePersistedContact'
import {usePersistedOrderRequests} from '../../../common/react-query/hooks/queries/usePersistedOrderRequests'
import {useOrderIntakeStyles} from '../../../Hooks/OrderIntake/useOrderIntakeStyles'
import {useFeaturesState} from '../../../Organisms/Features'
import {useBulkCementOrderIntake} from '../../../Organisms/OrderIntake/BulkCementOrderIntake.provider'
import {generateGuid} from '../../../Organisms/OrderIntake/utils'
import {BulkOrderFormValues} from '../../BulkOrdersFormValues'
import {PersistedMaterialsAction} from '../../components/PersistedMaterialsDialog/PersistedMaterialsBanner'
import {PersistedMaterialsDialog} from '../../components/PersistedMaterialsDialog/PersistedMaterialsDialog'
import {ShowOrderSummarySection} from '../../components/ShowOrderSummaryBtn'
import {SummaryNoValidMaterials} from '../../components/SummaryNoValidMaterials'
import {DEFAULT_CUSTOMER_REFERENCE} from '../../declarations/constants'
import {ShippingType} from '../../declarations/OrderIntake.enums'
import {
  AddingNewMaterialMetadata,
  OrderRequest,
  OrderRequestGroupResponse,
  OrdersFormValidityState,
  QuantityType
} from '../../declarations/types'
import {useIsFeatureEnabledForSelectedCustomerCountry} from '../../FeatureFlags'
import {useBulkOrderFormChangeHandlerWithMethods} from '../../FormChangeHandlers'
import {useOrderIntakeData} from '../../hooks'
import {useActivePopup} from '../../hooks/useActivePopup'
import * as options from '../../Options'
import {isInvalidOrderIntakeOption} from '../../Options/query'
import {SplitLoadOrderIntakePayload} from '../containers/SplitLoad/buildSplitLoadPayload'
import {useHandleClose} from '../hooks/useHandleClose'
import {useMaterialEditorController} from '../hooks/useMaterialEditorController'
import {
  areAllCustomerReferencesSame,
  getMaterialOptionsByInvalidity,
  mergePersistedContact,
  mergePersistedOrders
} from '../utils'

import {DeliveryOrderIntakeRequestConfirmation} from './DeliveryOrderIntakeRequestConfirmation'
import {useSplitLoadSubmit} from './handlers/useSplitLoadSubmit'
import {useDeliverSubmit} from './handlers/useDeliverSubmit'
import {usePersistedMaterialsState} from '../hooks/usePersistedMaterialsState'
import {useFieldHandlers} from '../hooks/useFieldHandlers'
import {DeliverMainContent} from './DeliverMainContent'
import {DeliverSidebar} from './DeliverSidebar'

export const BulkCementDeliverOrderForm = () => {
  const classes = useOrderIntakeStyles()
  const {
    t,
    i18n: {language}
  } = useTranslation()
  const {
    customerId,
    userId,
    userPrimaryOrganisationalUnit,
    userFullName,
    userProfileCountry,
    features: {
      orderIntakeCustomerReferenceField,
      hasSiteContactNameAndPhone,
      isMaterialPersistency,
      isContactPersistency
    }
  } = useOrderIntakeData()

  const openEditForm = useRef(true)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const {getFeature} = useFeaturesState()
  const isSlotsManagementEnabled = getFeature('OrderIntakeSlotsDeliver')
  const useConfigurableSlots = getFeature('OrderIntakeUseConfigurableSlots')
  const applyMaterialEnteredDescription = getFeature('OrderIntakeMaterialEnteredDescription')
  const applyContractItemDescription = getFeature('OrderIntakeContractItemDescription')
  const isAutoConfirmed = getFeature('OrderIntakeSlotsDeliverAutoConfirmedOrderingWhenFree')
  const enableV2Options = getFeature('EnableV2Options')
  const isSplitLoadFeatureEnabled =
    useIsFeatureEnabledForSelectedCustomerCountry('OrderIntakeSplitLoad')

  const configuration = {
    isSlotsManagementEnabled,
    useConfigurableSlots,
    nextDayCollect: false,
    applyMaterialEnteredDescription,
    applyContractItemDescription
  }

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const isSplitLoadMode = searchParams.get('loadType') === 'splitLoad'

  // Redirect if user tries to access split load mode when feature is disabled
  useEffect(() => {
    if (isSplitLoadMode && !isSplitLoadFeatureEnabled) {
      // Remove the loadType parameter and redirect to regular delivery mode
      searchParams.delete('loadType')
      const newUrl = searchParams.toString()
        ? `${location.pathname}?${searchParams.toString()}`
        : location.pathname
      window.location.href = newUrl
    }
  }, [isSplitLoadMode, isSplitLoadFeatureEnabled, location.pathname, searchParams])

  const {setDeliveries, slotConfiguration, setOrderSummary, deliveries} = useBulkCementOrderIntake()
  const {
    isLoading: isLoadingV1,
    isError: isErrorV1,
    mutateAsync: mutateAsyncV1
  } = usePostOrderRequestDelivery()
  const {
    isLoading: isLoadingV2,
    isError: isErrorV2,
    mutateAsync: mutateAsyncV2
  } = usePostOrderRequestDeliveryV2()

  // Use multi-load options when in split load mode, otherwise use regular delivery options
  const {data: deliverOptions} = isSplitLoadMode
    ? options.useMultiLoadDeliveryOptions(configuration, customerId)
    : options.useDeliveryOptions(configuration, customerId)
  const defaultBusinessLine = options.getBusinessLine(deliverOptions)

  const siteOptions = values(deliverOptions)
  const methods = useForm<BulkOrderFormValues>({
    mode: 'all',
    defaultValues: {
      selectedSite: siteOptions[0],
      orders: [],
      driverInstructions: '',
      isConfirmationMailChecked: true,
      contact: {
        mainPhone: undefined,
        fullName: undefined,
        backupPhone: undefined,
        carbonCopyEmail: undefined
      },
      isOrderRequestConfirmed: false,
      isBackupPhoneVisible: false
    }
  })

  const {
    fields: orderFields,
    append: appendOrder,
    remove: deleteOrder,
    replace: replaceOrders
  } = useFieldArray({
    control: methods.control,
    name: 'orders'
  })

  const {selectedSite, orders} = methods.watch()

  const siteNumber = methods.watch('selectedSite.shippingAddress.siteNumber')
  const initialOrderCustomerReference = methods.watch('orders.0.payload.customerReference')
  const selectedSplitLoadMaterialsFromParentForm = methods.watch('selectedSplitLoadMaterials')
  const savedBomKeysFromParentForm = methods.watch('savedBomKeys')
  const {data: persistedContact} = usePersistedContact(siteNumber)
  const {data: persistedOrderRequests, isFetching: isOrderRequestsCacheLoading} =
    usePersistedOrderRequests(siteNumber)
  const {data: defaultDelivery} = options.useDefaultDelivery(customerId, configuration, siteNumber)

  const orderSessionEventId = useRef(generateGuid()).current

  const formChangeHandlers = useBulkOrderFormChangeHandlerWithMethods(methods)

  const persistedMaterials = usePersistedMaterialsState({
    selectedSite,
    orders,
    defaultOrderRequest: defaultDelivery as OrderRequest,
    isSlotsManagementEnabled,
    useConfigurableSlots,
    slotConfiguration,
    persistedOrderRequests,
    shippingType: ShippingType.DELIVER,
    userId,
    userProfileCountry,
    userPrimaryOrganisationalUnit,
    customerId,
    siteNumber,
    defaultBusinessLine
  })

  useEffect(() => {
    const resolvedSelectedSite = selectedSite ?? deliverOptions?.[siteNumber]

    if (
      !resolvedSelectedSite ||
      !deliverOptions ||
      !userFullName ||
      !defaultDelivery ||
      isInvalidOrderIntakeOption(defaultDelivery)
    )
      return

    const orderRequestBySiteNumber = orders.find((or) => or.payload.siteNumber === siteNumber)
    const orderRequests = orderRequestBySiteNumber ? [orderRequestBySiteNumber] : [defaultDelivery]
    const contact = mergePersistedContact(
      resolvedSelectedSite,
      persistedContact,
      isContactPersistency
    )

    methods.setValue(
      'isConfirmationMailChecked',
      !first(persistedOrderRequests)?.isSendingConfirmationEmailUnChecked || true
    )

    formChangeHandlers.onSiteChange(siteNumber, deliverOptions, defaultDelivery)

    replaceOrders(
      orderRequests.map((orderRequest) => ({
        ...orderRequest,
        payload: {
          ...orderRequest.payload,
          customerReference: orderRequest.payload.customerReference || DEFAULT_CUSTOMER_REFERENCE
        },
        initialFullName: userFullName
      }))
    )

    methods.reset({
      ...methods.getValues(),
      isOrderRequestConfirmed: false,
      contact: {
        mainPhone: contact.mainPhone
          ? (parsePhoneNumberFromString(contact.mainPhone, userProfileCountry as CountryCode)
              ?.number as string)
          : '',
        backupPhone: contact.backupPhone
          ? (parsePhoneNumberFromString(contact.backupPhone, userProfileCountry as CountryCode)
              ?.number as string)
          : '',
        fullName: contact.fullName ? contact.fullName : userFullName,
        carbonCopyEmail: contact.carbonCopyEmail,
        siteContactName: hasSiteContactNameAndPhone ? contact.siteContactName : ''
      }
    })

    persistedMaterials.refreshValidMaterials()
  }, [
    siteNumber,
    persistedContact,
    persistedOrderRequests,
    userFullName,
    defaultDelivery,
    deliverOptions
  ])

  const [showConfirmationStatusScreen, setShowConfirmationStatusScreen] = useState<boolean>(false)
  const [editorActivationTimeStamp, setEditorActivationTimeStamp] = useState<string>()
  const [isAddingNewMaterial, setIsAddingNewMaterial] = useState<AddingNewMaterialMetadata>({
    isAddingNewMaterial: false,
    newMaterialIndex: -1
  })
  const [selectedMaterialIndex, setSelectedMaterialIndex] = useState<number>(-1)
  const [placeOrderStatus, setPlaceOrderStatus] = useState<boolean>(false)
  const [orderSummaryDataArray, setOrderSummaryDataArray] = useState<OrderRequestGroupResponse[]>(
    []
  )
  const [splitLoadPayloads, setSplitLoadPayloads] = useState<SplitLoadOrderIntakePayload[]>([])
  const [ordersFormValidity, setOrdersFormValidity] = useState<OrdersFormValidityState[]>([])
  const [isEditOrderButtonClicked, setIsEditOrderButtonClicked] = useState<boolean>(false)

  React.useEffect(() => {
    if (
      isAddingNewMaterial.isAddingNewMaterial &&
      orderFields.length === isAddingNewMaterial.newMaterialIndex + 1
    ) {
      const index = orderFields.length - 1
      handleEditorActivation(orderFields[index].id, 'details', index)
      return
    }

    if (openEditForm.current && orderFields?.length === 1) {
      openEditForm.current = false
      editFirstMaterial()
    }
  }, [isAddingNewMaterial, openEditForm])

  React.useEffect(() => {
    setSelectedMaterialIndex(0)
    materialEditorController.setChanges(false)
    openEditForm.current = true
    setOrdersFormValidity([])
    setIsAddingNewMaterial({isAddingNewMaterial: false, newMaterialIndex: -1})
  }, [selectedSite])

  const isAllOrdersValid = useMemo(
    () => ordersFormValidity.every((validity) => (validity ? !validity.isFormInvalid : false)),
    [ordersFormValidity, orderFields]
  )

  const editFirstMaterial = () => {
    const index = 0
    handleEditorActivation(orderFields[index].id, 'details', index)
  }

  const handleClose = useHandleClose(
    orders,
    setShowConfirmationStatusScreen,
    methods.reset,
    customerId
  )

  const materialEditorController = useMaterialEditorController()

  const [activePopup, setActivePopup] = useActivePopup(
    materialEditorController.setActiveOrderBox,
    materialEditorController.activeMaterialForm,
    isEditOrderButtonClicked
  )

  const shouldUseV2 = !!enableV2Options
  const isLoading = shouldUseV2 ? isLoadingV2 : isLoadingV1
  const isError = shouldUseV2 ? isErrorV2 : isErrorV1
  const shouldUseV2ForSplitLoad = shouldUseV2 && isSplitLoadMode

  // Split load submit handler (must be called before early return)
  const businessLineCode =
    defaultDelivery && !isInvalidOrderIntakeOption(defaultDelivery)
      ? defaultDelivery.payload.businessLineCode
      : ''

  const {onSplitLoadSubmit: createSplitLoadSubmit} = useSplitLoadSubmit({
    customerId,
    defaultDeliveryBusinessLineCode: businessLineCode,
    hasSiteContactNameAndPhone,
    defaultQuantityType:
      defaultDelivery && !isInvalidOrderIntakeOption(defaultDelivery)
        ? defaultDelivery.payload.capacity.quantityType
        : '',
    orderIntakeCustomerReferenceField,
    isConfigurableSlots: useConfigurableSlots,
    isAutoConfirmed,
    mutateAsync: mutateAsyncV1,
    setDeliveries,
    setPlaceOrderStatus,
    setOrderSummaryDataArray,
    setOrderSummary,
    setShowConfirmationStatusScreen,
    setSplitLoadPayloads,
    mutateAsyncV2,
    shouldUseV2: shouldUseV2ForSplitLoad,
    orgUnitId:
      selectedSite?.materials[deliveries[0]?.payload?.materialEnteredNumber]?.[0].material
        ?.orgUnitId ??
      userPrimaryOrganisationalUnit ??
      '',
    country: userProfileCountry ?? selectedSite?.shippingAddress.country ?? '',
    allocatorInstructions: deliveries[0]?.payload?.orderAllocatorInstructions ?? '',
    siteId: selectedSite?.shippingAddress.siteId ?? selectedSite?.shippingAddress.siteNumber ?? ''
  })
  const onSplitLoadSubmit = createSplitLoadSubmit(materialEditorController)

  const fieldHandlers = useFieldHandlers({
    methods,
    formChangeHandlers,
    materialEditorController,
    selectedSite,
    setEditorActivationTimeStamp,
    setSelectedMaterialIndex,
    t
  })

  if (!deliverOptions || !selectedSite || !defaultDelivery) return null

  const addDelivery = () => {
    const currentOrdersCount = orders.length
    const lastOrderItem = orders[orders.length - 1]
    appendOrder({...lastOrderItem, payload: {...lastOrderItem.payload, haulerInfo: {}}})
    trackEvent('hubOrderIntakeAddOrderRequest')
    setIsAddingNewMaterial({isAddingNewMaterial: true, newMaterialIndex: currentOrdersCount})
    methods.setValue('isOrderRequestConfirmed', true)
  }

  const removeDelivery = (index: number) => {
    deleteOrder(index)
    trackEvent('hubOrderIntakeRemoveOrderRequest')

    materialEditorController.setActiveOrderBox(undefined)
  }

  const quantityType =
    (defaultDelivery as OrderRequest)?.payload?.capacity?.quantityType ?? QuantityType.TruckCapacity
  const quantityUom = first(
    selectedSite.materials[(defaultDelivery as OrderRequest)?.payload?.materialEnteredNumber]
  )?.truckCapacity.capacityUom

  const {onSubmit} = useDeliverSubmit({
    methods,
    orders,
    replaceOrders,
    selectedSite,
    userProfileCountry,
    userPrimaryOrganisationalUnit,
    businessLineCode,
    defaultBusinessLine,
    quantityType,
    orderIntakeCustomerReferenceField,
    hasSiteContactNameAndPhone,
    shouldUseV2,
    isConfigurableSlots: useConfigurableSlots,
    isAutoConfirmed,
    language,
    orderSessionEventId,
    mutateAsyncV1,
    mutateAsyncV2,
    setDeliveries,
    setPlaceOrderStatus,
    setOrderSummaryDataArray,
    setOrderSummary,
    setShowConfirmationStatusScreen
  })

  const {
    onCustomerReferenceChange,
    onQuantityChange,
    onDriverInstructionsChange,
    onDispatchNotesChange,
    onBomKeyChange,
    onMaterialChange,
    onSplitLoadMaterialsSelected,
    handleEditorActivation,
    handleStatusChange
  } = fieldHandlers

  const applyPersistedMaterials = () => {
    openEditForm.current = true
    const orderRequestBySiteNumber = orders.find((or) => or.payload.siteNumber === siteNumber)

    const validPersistedOrderRequests = persistedMaterials.getValidPersistedOrderRequests()

    const orderRequests = mergePersistedOrders(
      orderRequestBySiteNumber ? orderRequestBySiteNumber : (defaultDelivery as OrderRequest),
      selectedSite,
      validPersistedOrderRequests,
      false // Deliver form doesn't use this slot management feature
    )

    if (orderRequests) {
      replaceOrders(
        orderRequests.map((orderRequest) => ({
          ...orderRequest,
          payload: {
            ...orderRequest.payload,
            customerReference: orderRequest.payload.customerReference || DEFAULT_CUSTOMER_REFERENCE
          }
        }))
      )
    }

    persistedMaterials.trackReviewPersistedMaterials()
  }

  const onClosePersistedMaterialsBanner = (action: PersistedMaterialsAction) => {
    const result = persistedMaterials.onCloseBanner(action)
    if (result === 'apply') {
      applyPersistedMaterials()
    }
  }

  const onClosePersistedMaterials = (apply: boolean) => {
    const shouldApply = persistedMaterials.onCloseDialog(apply)
    if (shouldApply) {
      applyPersistedMaterials()
    }
  }

  const handleSummarySectionEditButtonClick = (index: number) => {
    handleEditorActivation(orderFields[index].id, 'details', index)
  }

  const isCustomerReferenceSame = areAllCustomerReferencesSame(orders)
  const validMaterialOptions = getMaterialOptionsByInvalidity(selectedSite.materials, false)
  const invalidMaterialOptions = getMaterialOptionsByInvalidity(selectedSite.materials, true)
  const noValidMaterial =
    (invalidMaterialOptions.length >= 1 && isEmpty(validMaterialOptions)) ||
    isInvalidOrderIntakeOption(defaultDelivery)

  // we cannot have form in form, if so, it won't work and submit button will trigger upper form
  // so when there is activeMaterialForm, we disable the form and render only div
  const isMainFormActive = !materialEditorController.activeMaterialForm
  const Form = !materialEditorController.activeMaterialForm ? 'form' : 'div'

  // Determine which submit handler to use based on split load mode
  const submitHandler = isSplitLoadMode ? onSplitLoadSubmit : onSubmit

  return (
    <FormProvider {...methods}>
      <Form onSubmit={isMainFormActive ? methods.handleSubmit(submitHandler) : undefined}>
        {showConfirmationStatusScreen && (
          <DeliveryOrderIntakeRequestConfirmation
            businessLine={defaultBusinessLine}
            quantityType={quantityType}
            quantityUom={quantityUom ?? ''}
            onClose={handleClose}
            groupOrderSummaryDataArray={orderSummaryDataArray ?? []}
            splitLoadPayloads={
              isSplitLoadMode && splitLoadPayloads.length > 0 ? splitLoadPayloads : undefined
            }
            isLoading={isLoading}
            isError={isError}
          />
        )}

        {!showConfirmationStatusScreen && (
          <Grid container style={{padding: 0}}>
            <Grid item xs={12}>
              <Grid container justifyContent="space-between">
                {/* Persisted materials dialog*/}
                {isMaterialPersistency &&
                  persistedOrderRequests &&
                  persistedMaterials.showDialog && (
                    <Dialog open={true} onClose={() => onClosePersistedMaterials(false)}>
                      <DialogContent>
                        <PersistedMaterialsDialog
                          persistedMaterials={persistedOrderRequests}
                          validMaterials={persistedMaterials.validMaterials}
                          existsValidMaterials={persistedMaterials.existsValidPersistedMaterials}
                          onClose={onClosePersistedMaterials}
                        />
                      </DialogContent>
                    </Dialog>
                  )}

                <Grid
                  container
                  className={classes.container}
                  justifyContent="space-between"
                  data-test-id="order-intake-form-container"
                  style={{padding: 0}}
                >
                  <Grid container spacing={4}>
                    {/* LEFT PANEL */}
                    <Hidden mdDown={!!activePopup}>
                      <DeliverMainContent
                        methods={methods}
                        siteOptions={siteOptions}
                        deliverOptions={deliverOptions}
                        defaultDelivery={defaultDelivery}
                        formChangeHandlers={formChangeHandlers}
                        customerId={customerId}
                        applyContractItemDescription={applyContractItemDescription}
                        applyMaterialEnteredDescription={applyMaterialEnteredDescription}
                        isMaterialPersistency={isMaterialPersistency}
                        persistedOrderRequests={persistedOrderRequests}
                        onClosePersistedMaterialsBanner={onClosePersistedMaterialsBanner}
                        selectedSite={selectedSite}
                        orderFields={orderFields}
                        materialEditorController={materialEditorController}
                        isSlotsManagementEnabled={isSlotsManagementEnabled}
                        useConfigurableSlots={useConfigurableSlots}
                        slotConfiguration={slotConfiguration}
                        ordersFormValidity={ordersFormValidity}
                        isAllOrdersValid={isAllOrdersValid}
                        isOrderRequestsCacheLoading={isOrderRequestsCacheLoading}
                        noValidMaterial={noValidMaterial}
                        defaultBusinessLine={defaultBusinessLine}
                        initialOrderCustomerReference={initialOrderCustomerReference}
                        addDelivery={addDelivery}
                        removeDelivery={removeDelivery}
                        handleEditorActivation={handleEditorActivation}
                        setIsEditOrderButtonClicked={setIsEditOrderButtonClicked}
                        isMobile={isMobile}
                        t={t}
                        classes={classes}
                        persistedMaterialsBanner={persistedMaterials}
                        orders={orders}
                      />
                    </Hidden>

                    {/* RIGHT PANEL*/}
                    <Hidden smDown={!activePopup}>
                      <DeliverSidebar
                        placeOrderStatus={placeOrderStatus}
                        noValidMaterial={noValidMaterial}
                        isSplitLoadMode={isSplitLoadMode}
                        isAllOrdersValid={isAllOrdersValid}
                        activePopup={activePopup}
                        setActivePopup={setActivePopup}
                        materialEditorController={materialEditorController}
                        selectedMaterialIndex={selectedMaterialIndex}
                        orderFields={orderFields}
                        orders={orders}
                        isCustomerReferenceSame={isCustomerReferenceSame}
                        isSlotsManagementEnabled={isSlotsManagementEnabled}
                        useConfigurableSlots={useConfigurableSlots}
                        editorActivationTimeStamp={editorActivationTimeStamp}
                        savedBomKeysFromParentForm={savedBomKeysFromParentForm}
                        selectedSplitLoadMaterialsFromParentForm={
                          selectedSplitLoadMaterialsFromParentForm
                        }
                        ordersFormValidity={ordersFormValidity}
                        setOrdersFormValidity={setOrdersFormValidity}
                        onCustomerReferenceChange={onCustomerReferenceChange}
                        onQuantityChange={onQuantityChange}
                        onDriverInstructionsChange={onDriverInstructionsChange}
                        onDispatchNotesChange={onDispatchNotesChange}
                        onBomKeyChange={onBomKeyChange}
                        onMaterialChange={onMaterialChange}
                        onSplitLoadMaterialsSelected={onSplitLoadMaterialsSelected}
                        handleStatusChange={handleStatusChange}
                        removeDelivery={removeDelivery}
                        handleEditorActivation={handleEditorActivation}
                        handleSummarySectionEditButtonClick={handleSummarySectionEditButtonClick}
                        formChangeHandlers={formChangeHandlers}
                        customerId={customerId}
                        quantityType={quantityType}
                        classes={classes}
                      />
                    </Hidden>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* MOBILE FOOTER*/}
            <Hidden smUp smDown={!!activePopup}>
              {!noValidMaterial ? (
                <ShowOrderSummarySection
                  isMobile={isMobile}
                  onOpenOrderSummary={() => setActivePopup('OrderSummary')}
                  disabled={
                    !methods.formState.isValid ||
                    noValidMaterial ||
                    isOrderRequestsCacheLoading ||
                    !isAllOrdersValid
                  }
                />
              ) : (
                <Grid item xs={12}>
                  <Card>
                    <SummaryNoValidMaterials invalidMaterialOptions={invalidMaterialOptions} />
                  </Card>
                </Grid>
              )}
            </Hidden>
          </Grid>
        )}
      </Form>
    </FormProvider>
  )
}
